import { useState, useEffect } from 'react';
import { useApp, useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import notificationService from '../../services/notificationService';
import type { Notification } from '../../services/webSocketService';
import webSocketService from '../../services/webSocketService';
import { 
  Bell, 
  Search, 
  User, 
  Menu,
  Sun,
  Moon,
  LogOut,
  Settings,
  ChevronDown,
  Wifi,
  WifiOff
} from 'lucide-react';

export function Header() {
  const { state, logout, toggleSidebar, setTheme } = useApp();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [wsConnected, setWsConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<string>('disconnected');
  const [removingNotifications, setRemovingNotifications] = useState<Set<number>>(new Set());

  // Subscribe to notifications
  useEffect(() => {
    const unsubscribe = notificationService.subscribe((newNotifications) => {
      setNotifications(newNotifications);
    });

    return unsubscribe;
  }, []);

  // Subscribe to WebSocket connection status
  useEffect(() => {
    const subscription = webSocketService.connectionStatus().subscribe({
      next: (connected: boolean) => {
        setWsConnected(connected);
        setConnectionStatus(webSocketService.getConnectionStatus());
      },
      error: (error: unknown) => {
        console.error('WebSocket connection error:', error);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const toggleTheme = () => {
    setTheme(state.theme === 'light' ? 'dark' : 'light');
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      toast.error(`Logout failed: ${error}`);
      // Still navigate to login even if logout fails
      navigate('/login');
    }
  };

  const unreadCount = notificationService.getUnreadCount();
  
  const formatNotificationTime = (createdAt: string) => {
    const now = new Date();
    // Handle both ISO string and timestamp formats
    const notificationTime = new Date(createdAt);
    
    // Check if the date parsing failed
    if (Number.isNaN(notificationTime.getTime())) {
      //console.warn('Invalid date format for notification:', createdAt);
      return 'Just now';
    }
    
    // Calculate difference in seconds for more accurate "just now" detection
    const diffInSeconds = Math.floor((now.getTime() - notificationTime.getTime()) / 1000);
    
    // Handle edge case for very recent notifications or future timestamps
    if (diffInSeconds <= 30) return 'Just now'; // Within 30 seconds
    
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes} min ago`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
  };

  const handleNotificationClick = async (notification: Notification) => {
    // Add to removing set for animation
    setRemovingNotifications(prev => new Set(prev).add(notification.id));
    
    // Wait for animation to complete
    setTimeout(async () => {
      try {
        // Mark as read
        await notificationService.markAsRead(notification.id);
        
        // Remove from removing set
        setRemovingNotifications(prev => {
          const newSet = new Set(prev);
          newSet.delete(notification.id);
          return newSet;
        });
        
        // Navigate to relevant page based on notification type
        if (notification.type === 'NEW_BOOKING' && notification.bookingId) {
          // Navigate to reservations with specific booking highlighted
          navigate(`/reservations?bookingId=${notification.bookingId}`);
        } else {
          // Default navigation to reservations page
          navigate('/reservations');
        }
        
        // Close the dropdown
        setShowNotifications(false);
      } catch (error) {
        //console.error('Failed to handle notification click:', error);
        // Remove from removing set on error
        setRemovingNotifications(prev => {
          const newSet = new Set(prev);
          newSet.delete(notification.id);
          return newSet;
        });
        // Still navigate even if marking as read fails
        navigate('/reservations');
        setShowNotifications(false);
      }
    }, 300); // Animation duration
  };

  return (
    <header 
      className="h-16 shadow-md border-b border-gray-200/60 flex items-center justify-between px-6"
    >
      {/* Left section */}
      <div className="flex items-center space-x-4">
        {/* Mobile menu button */}
        <button
          onClick={toggleSidebar}
          className="p-2 rounded-lg hover:bg-muted transition-colors md:hidden"
        >
          <Menu className="h-5 w-5" />
        </button>

        {/* Search */}
        <div className="relative hidden sm:block">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search..."
            className="pl-10 pr-4 py-2 w-64 border border-gray-300 text-gray-900 focus:ring-2 focus:border-transparent outline-none transition-all duration-200 hover:border-gray-400"
            style={{
              borderRadius: '12px',
              background: 'rgba(255, 255, 255, 0.95)',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
            }}
            onFocus={(e) => {
              e.target.style.boxShadow = '0 0 0 3px rgba(212, 175, 55, 0.4), 0 1px 3px 0 rgba(0, 0, 0, 0.1)';
            }}
            onBlur={(e) => {
              e.target.style.boxShadow = '0 1px 3px 0 rgba(0, 0, 0, 0.1)';
            }}
          />
        </div>
      </div>

      {/* Right section */}
      <div className="flex items-center space-x-4">
        {/* Theme toggle */}
        <button
          onClick={toggleTheme}
          className="p-2 rounded-lg transition-all duration-200 hover:shadow-md"
          style={{
            borderRadius: '12px',
            backgroundColor: 'rgba(212, 175, 55, 0.08)',
            border: '1px solid rgba(212, 175, 55, 0.2)'
          }}
          title={state.theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(212, 175, 55, 0.12)';
            e.currentTarget.style.transform = 'translateY(-1px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(212, 175, 55, 0.08)';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          {state.theme === 'light' ? (
            <Moon className="h-5 w-5 text-gray-600" />
          ) : (
            <Sun className="h-5 w-5 text-gray-600" />
          )}
        </button>

        {/* Notifications */}
        <div className="relative">
          <button
            onClick={() => setShowNotifications(!showNotifications)}
            className="p-2 rounded-lg transition-all duration-200 relative hover:shadow-md"
            style={{
              borderRadius: '12px',
              backgroundColor: 'rgba(212, 175, 55, 0.08)',
              border: '1px solid rgba(212, 175, 55, 0.2)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(212, 175, 55, 0.12)';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(212, 175, 55, 0.08)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <Bell className="h-5 w-5 text-gray-600" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 h-4 w-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-medium ">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>

          {/* Notifications dropdown */}
          {showNotifications && (
            <div className="absolute right-0 mt-2 w-80 bg-popover rounded-lg shadow-[0_35px_60px_-15px_rgba(0,0,0,0.3)]  bg-white z-50">
              <div className="p-4 flex items-center justify-between">
                <h3 className="text-sm font-medium text-popover-foreground">Notifications</h3>
                {(() => {
                  if (wsConnected) {
                    return (
                      <div className="flex items-center gap-1 text-green-600">
                        <Wifi className="w-3 h-3" />
                        <span className="text-xs">Live</span>
                      </div>
                    );
                  }
                  if (connectionStatus === 'fallback') {
                    return (
                      <div className="flex items-center gap-1 text-yellow-600">
                        <WifiOff className="w-3 h-3" />
                        <span className="text-xs">Fallback</span>
                      </div>
                    );
                  }
                  return (
                    <div className="flex items-center gap-1 text-red-600">
                      <WifiOff className="w-3 h-3" />
                      <span className="text-xs">Offline</span>
                    </div>
                  );
                })()}
              </div>
              <div className="max-h-96 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                {(() => {
                  const unreadNotifications = notifications.filter(n => !n.isRead);
                  if (unreadNotifications.length > 0) {
                    return unreadNotifications.map((notification) => (
                      <button
                        key={notification.id}
                        type="button"
                        className={`w-full text-left p-4 hover:bg-gray-200 mb-2 shadow-lg last:border-b-0 bg-blue-50 transition-all duration-300 ${
                          removingNotifications.has(notification.id) 
                            ? 'opacity-0 transform scale-95' 
                            : 'opacity-100 transform scale-100'
                        }`}
                        onClick={() => handleNotificationClick(notification)}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <p className="text-sm font-medium text-foreground">
                              {notification.type === 'NEW_BOOKING' ? '🔔 New Booking' : notification.type}
                            </p>
                            <p className="text-sm text-muted-foreground mt-1">{notification.message}</p>
                            <p className="text-xs text-muted-foreground mt-1">{formatNotificationTime(notification.createdAt)}</p>
                          </div>
                          <div className="w-2 h-2 bg-blue-500 rounded-full mt-1"></div>
                        </div>
                      </button>
                    ));
                  }
                  return (
                    <div className="p-4 text-center text-muted-foreground">
                      <p className="text-sm">No new notifications</p>
                    </div>
                  );
                })()}
              </div>
              <div className="p-2 border-t border-border space-y-2">
                <button 
                  className="w-full text-sm text-red-600 hover:text-red-700 py-2"
                  onClick={() => {
                    notificationService.clearNotifications();
                    setShowNotifications(false);
                  }}
                >
                  Mark all as read
                </button>
                {connectionStatus === 'fallback' && (
                  <button 
                    className="w-full text-sm text-yellow-600 hover:text-yellow-700 py-2 border border-yellow-200 rounded"
                    onClick={() => {
                      webSocketService.reconnect();
                    }}
                  >
                    Retry Connection
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* User menu */}
        <div className="relative">
          <button
            onClick={() => setShowUserMenu(!showUserMenu)}
            className="flex items-center space-x-2 p-2 rounded-lg hover:bg-muted transition-colors"
          >
            <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
              <User className="h-4 w-4 text-primary-foreground" />
            </div>
            {user && (
              <span className="hidden sm:block text-sm font-medium text-foreground">
                {user.name}
              </span>
            )}
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          </button>

          {/* User menu dropdown */}
          {showUserMenu && (
            <div className="absolute bg-white right-0 mt-2 w-48 bg-popover rounded-lg shadow-lg z-50">
              {user && (
                <div className="p-4 border-b border-border rounded-lg">
                  <p className="text-sm font-medium text-popover-foreground">{user.name}</p>
                  <p className="text-sm text-muted-foreground">{user.email}</p>
                  <span className="inline-block px-2 py-1 text-xs bg-primary/10 text-primary rounded-full mt-1">
                    {user.role}
                  </span>
                </div>
              )}
              
              <div className="py-1">
                <button className="flex items-center w-full px-4 py-2 text-sm text-popover-foreground hover:bg-[#04070640]"
                onClick={() => navigate('/settings')}
                >
                  <Settings className="h-4 w-4 mr-3" />
                  Settings
                </button>
                <button 
                  onClick={handleLogout}
                  className="flex items-center w-full px-4 py-2 text-sm text-popover-foreground hover:bg-[#04070628] text-red-600"
                >
                  <LogOut className="h-4 w-4 mr-3" />
                  Logout
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Click outside handlers */}
      {(showUserMenu || showNotifications) && (
        <button
          type="button"
          className="fixed inset-0 z-40 bg-transparent cursor-default"
          onClick={() => {
            setShowUserMenu(false);
            setShowNotifications(false);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setShowUserMenu(false);
              setShowNotifications(false);
            }
          }}
          aria-label="Close menu"
          style={{ background: 'transparent' }}
        />
      )}
    </header>
  );
}
